import React from 'react';
import { Modal, Carousel } from 'react-bootstrap';
import 'app/components/WhatsNew/whatsNew.scss';
import { connect } from 'react-redux';
import AppHistory from '__common/modules/history';
import { CLOSE_WHATS_NEW, DISMISS_WHATS_NEW, MARK_WHATS_NEW_SEEN } from './WhatsNewActions';
import { WHATS_NEW_DISMISS_KEY } from './whatsNewHelpers';

type Props = {
  user: userState,
  dispatch: Function,
  whatsNew: whatsNewState,
};

type State = {
  dontShowAgain: boolean,
  activeIndex: number,
  shakeModal: boolean,
};

class WhatsNew extends React.Component<Props, State> {
  unlistenHistory: Function;
  shakeTimer: number;

  constructor(props) {
    super(props);
    this.state = {
      dontShowAgain: false,
      activeIndex: 0,
      shakeModal: false,
    };
  }

  componentDidMount() {
    this.unlistenHistory = AppHistory.listen(() => {
      if (this.props.whatsNew.open) {
        this.props.dispatch(CLOSE_WHATS_NEW());
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.whatsNew.open && this.props.whatsNew.open) {
      this.setState({ dontShowAgain: false, activeIndex: 0, shakeModal: false });
    }
  }

  componentWillUnmount() {
    if (this.unlistenHistory) {
      this.unlistenHistory();
    }
    if (this.shakeTimer) {
      window.clearTimeout(this.shakeTimer);
    }
  }

  handleOutsideClick = () => {
    if (this.shakeTimer) {
      window.clearTimeout(this.shakeTimer);
    }

    this.setState({ shakeModal: false }, () => {
      window.requestAnimationFrame(() => {
        this.setState({ shakeModal: true });
        this.shakeTimer = window.setTimeout(() => {
          this.setState({ shakeModal: false });
        }, 1180);
      });
    });
  }

  toggleDontShowAgain = () => {
    this.setState(prevState => ({ dontShowAgain: !prevState.dontShowAgain }));
  }

  handleSelect = (index: number) => {
    this.setState({ activeIndex: index });
  }

  next = () => {
    this.setState(prevState => ({ activeIndex: prevState.activeIndex + 1 }));
  }

  prev = () => {
    this.setState(prevState => ({ activeIndex: Math.max(prevState.activeIndex - 1, 0) }));
  }

  cancel = () => {
    const { dispatch, user, whatsNew: { latestMajorReleaseId } } = this.props;

    if (this.state.dontShowAgain) {
      if (latestMajorReleaseId) {
        localStorage.setItem(WHATS_NEW_DISMISS_KEY, String(latestMajorReleaseId));
      }
      if (user && user.isAuthenticated) {
        dispatch(DISMISS_WHATS_NEW());
      }
      dispatch(MARK_WHATS_NEW_SEEN());
    }

    dispatch(CLOSE_WHATS_NEW());
    this.setState({ dontShowAgain: false, activeIndex: 0, shakeModal: false });
  }

  renderSlide(slide: WhatsNewSlide) {
    const hasText = !!(
      slide.description
      || slide.features_heading
      || (slide.features && slide.features.length > 0)
    );
    const imageOnly = !!slide.image && !hasText;

    return (
      <Carousel.Item key={slide.id}>
        <div className={`whats-new-slide${imageOnly ? ' whats-new-slide--image-only' : ''}`}>
          {imageOnly ? (
            <div className="whats-new-slide-image-only">
              <a
                href={slide.image}
                target="_blank"
                rel="noopener noreferrer"
                title="Open image in new tab"
              >
                <img src={slide.image} alt={slide.title} />
              </a>
            </div>
          ) : (
            <div className="whats-new-slide-main">
              <div className="whats-new-slide-text">
                {slide.description && (
                  <div className="whats-new-slide-intro">{slide.description}</div>
                )}
                {slide.features_heading && (
                  <div className="whats-new-slide-subheading">{slide.features_heading}</div>
                )}
                {slide.features && slide.features.length > 0 && (
                  <ul className="whats-new-feature-list">
                    {slide.features.map(feature => (
                      <li className="whats-new-feature-item" key={feature.id}>
                        <span className="whats-new-feature-title">{feature.title}</span>
                        {feature.description && (
                          <span className="whats-new-feature-description"> {feature.description}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {slide.image && (
                <div className="whats-new-slide-image">
                  <a
                    href={slide.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open image in new tab"
                  >
                    <img src={slide.image} alt={slide.title} />
                  </a>
                </div>
              )}
            </div>
          )}
          {slide.action_url && (
            <a
              className="whats-new-action-link"
              href={slide.action_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {slide.action_label || 'Learn more'}
            </a>
          )}
        </div>
      </Carousel.Item>
    );
  }

  render() {
    const { whatsNew: { open, releases } } = this.props;
    const { dontShowAgain, activeIndex, shakeModal } = this.state;
    const slides = (releases || []).reduce(
      (acc: WhatsNewSlide[], release) => acc.concat(release.slides || []),
      [],
    );
    const total = slides.length;
    const isLast = activeIndex >= total - 1;

    return (
      <Modal
        show={open}
        onHide={this.handleOutsideClick}
        backdrop={true}
        keyboard={true}
        aria-labelledby="whats-new-title"
        dialogClassName="whats-new-modal"
        className="whats-new-modal-root"
        backdropClassName="whats-new-modal-backdrop"
      >
        <div className={`whats-new-panel${shakeModal ? ' whats-new-panel--shake' : ''}`}>
          <button
            type="button"
            className="whats-new-close"
            aria-label="Close"
            onClick={this.cancel}
          >
            &times;
          </button>
          <div className="whats-new-title" id="whats-new-title">NEW ON U-BUILDER&trade;</div>
          {total > 0 ? (
            <React.Fragment>
              <Carousel
                activeIndex={activeIndex}
                onSelect={this.handleSelect}
                interval={null}
                controls={false}
                indicators={false}
                wrap={false}
                className="whats-new-carousel"
              >
                {slides.map(slide => this.renderSlide(slide))}
              </Carousel>
              {total > 1 && (
                <div className="whats-new-dots" role="tablist" aria-label="Slides">
                  {slides.map((slide, index) => (
                    <button
                      type="button"
                      key={slide.id}
                      className={`whats-new-dot${index === activeIndex ? ' whats-new-dot--active' : ''}`}
                      aria-label={`Go to slide ${index + 1}`}
                      aria-current={index === activeIndex}
                      onClick={() => this.handleSelect(index)}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          ) : (
            <div className="whats-new-empty">
              <i className="material-icons whats-new-empty-icon">campaign</i>
              <div className="whats-new-empty-title">No release notes yet</div>
              <div className="whats-new-empty-subtext">
                Check back soon &mdash; new features and updates will appear here.
              </div>
            </div>
          )}
          <div className="whats-new-footer">
            <label className="whats-new-checkbox">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={this.toggleDontShowAgain}
              />
              <span className="whats-new-checkbox-box" aria-hidden="true" />
              <span className="whats-new-checkbox-label">
                Don't show this again until the next major release
              </span>
            </label>
            {total > 0 && (
              <div className="whats-new-nav">
                {activeIndex > 0 && (
                  <button
                    type="button"
                    className="whats-new-nav-action"
                    onClick={this.prev}
                  >
                    &laquo; BACK
                  </button>
                )}
                <span className="whats-new-counter">{activeIndex + 1}/{total}</span>
                {isLast ? (
                  <button
                    type="button"
                    className="whats-new-nav-action"
                    onClick={this.cancel}
                  >
                    CLOSE WINDOW
                  </button>
                ) : (
                  <button
                    type="button"
                    className="whats-new-nav-action"
                    onClick={this.next}
                  >
                    NEXT &raquo;
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }
}

function mapStateToProps(appState) {
  return {
    whatsNew: appState.whatsNew,
    user: appState.user,
  };
}

export default connect(mapStateToProps)(WhatsNew);
